import { ViewType, ViewObject } from '../base';
import { toCanvas, toDataURL } from 'qrcode';

import { dataService } from '../../data';
import { QrCode } from '../objects/qrcode';

import { PlaceView } from './place';
import { TicketView } from './ticket';
import { TillView } from './till';
import { AskWaiterView } from './askwaiter';
import { PriceZoneView } from './pricezone';
import { FloorAreaView } from './floorarea';

interface TableThumbInfo {
    icon: string,
    color: string
}

type TableStatus = 'wait' | 'cnfg' | 'goto' | 'busy' | 'work' | 'free';

/** Options for table QR PNG generation (`QrCodeImageDataUrl`). */
export type QrCodeImageOptions = {
    /** Unpispas mark (or equivalent) as an image data URL; composed centered on the matrix. */
    logoDataUrl?: string | null;
};

/**
 * Pending guest payment request for one table (aggregated PAYCASH/PAYCARD asks).
 * One entry per mesa for ptf listing — not a persisted table.
 */
export type PayRequest = {
    table: QrCodeView;
    tickets: TicketView[];
    total: number;
    method: 'PAYCASH' | 'PAYCARD';
    since: Date | null;     // Oldest AskWaiter.created among payment requests of those tickets.
};

/****************************/
/* CLASS VIEW               */
/****************************/

export class _QrCodeView extends ViewObject<QrCode> {
    
    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get qrcode(): QrCode{
        return this.object;
    }
             
    constructor(object: QrCode, data: dataService){
        super(object, data);
    }

    override OnDestroy(): void {
        super.OnDestroy();
    }

    /**********************************/
    /* VIEW MANAGED INFORMATION        */
    /**********************************/

    private _validtickets: Array <TicketView> | null = null;
    private _unpaidtickets: Array <TicketView> | null = null;
    private _topaytickets: Array <TicketView> | null = null;
    private _paidtickets: Array <TicketView> | null = null;
    private _readytickets: Array <TicketView> | null = null;
    private _validrequests: Array <AskWaiterView> | null = null;
    private _tablestat: TableStatus | null = null;
    private _thumbinfo: TableThumbInfo | null = null;
    private _validdiners: number | null = null;
    private _isconnected: boolean | null = null;

    private RefreshView(targets: string[]){
        if (targets.includes('QRCODE')){
            this._validtickets = null;
            this._unpaidtickets = null;
            this._topaytickets = null;
            this._paidtickets = null;
            this._readytickets = null;
            this._validrequests = null;
            this._tablestat = null;
            this._thumbinfo = null;
            this._validdiners = null;
            this._isconnected = null;
            this.DoRefreshView();
        }

        if (targets.includes('TICKET')){
            this._validtickets = null;
            this._unpaidtickets = null;
            this._topaytickets = null;
            this._paidtickets = null;
            this._readytickets = null;
            this._tablestat = null;
            this._thumbinfo = null;
            this._validdiners = null;
            this.DoRefreshView();
        }

        if (targets.includes('ASKWAITER')){
            this._validrequests = null;
            this._tablestat = null;
            this._thumbinfo = null;
            this.DoRefreshView();
        }

        if (targets.includes('QRSCAN') || targets.includes('SESSION')){
            this._isconnected = null;
            this.DoRefreshView();
        }

        if (targets.includes('FLOORAREA') || targets.includes('DRAWITEM')){
            this.DoRefreshView();
        }
    }

    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void{
        const weakref = new WeakRef(this);

        // deregister of current subscriptions
        for (const _subsription of this._subscriptions){
            _subsription.unsubscribe();
        }
        this._subscriptions = [];

        // register to updates on contained items           
        this._subscriptions.push(
            this.qrcode.OnRefresh.subscribe(
            (targets: string[]) => {
                const self = weakref.deref();
                if (self){  // do not keep strong references in subscription
                    self.RefreshView(targets);
                }                
            })
        )    

        if (this.qrcode.place){
            this._subscriptions.push(
                this.qrcode.place.OnRefresh.subscribe(
                (targets: string[]) => {
                    const self = weakref.deref();
                    if (self){  // do not keep strong references in subscription
                        self.RefreshView(targets);
                    }                
                })
            )        
        }
    }

    /**********************************/
    /* ACTIVE CHILDREN INFORMATION    */
    /**********************************/   
    
    /**
     * Place for this table. QRCODE has no persistent `place` column — the
     * canonical link is QRSCAN.place (same fallback sync uses for guest).
     */
    get place(): PlaceView {
        const _place = this.qrcode.place ?? this.qrcode.qrscan?.place ?? null;
        return this.alives.get(_place) as PlaceView;
    }

    get area(): FloorAreaView | null {
        return this.alives.get(this.qrcode.area) as FloorAreaView | null;
    }

    set area(value: FloorAreaView | null) {
        this.qrcode.area = value?.object ?? null;
    }

    get till(): TillView | null {
        return this.alives.get(this.qrcode.till) as TillView | null;
    }

    set till(value: TillView | null) {
        this.qrcode.till = value?.object ?? null;
    }

    get pricezone(): PriceZoneView | null {
        return this.alives.get(this.qrcode.pricezone) as PriceZoneView | null;
    }

    set pricezone(value: PriceZoneView | null) {
        this.qrcode.pricezone = value?.object ?? null;
    }

    /** Tickets already related on this table in storage (guest graph is session-scoped). */
    get tickets(): Array <TicketView> {
        if (this._validtickets === null){
            const _validtickets: Array <TicketView> = [];
            for (const _ticket of this.qrcode.tickets){
                const _ticketview = this.alives.get(_ticket) as TicketView;
                if (_ticketview){
                    _validtickets.push(_ticketview);
                }
            }
            this._validtickets = _validtickets;
        }

        return this._validtickets;
    }

    /**
     * Unpaid recent tickets that can still be selected for payment (guest bill).
     * Excludes tickets already awaiting confirmation (`IsToPay`) — those are
     * {@link ToPayTickets}.
     */
    get UnpaidTickets(): Array <TicketView> {
        if (this._unpaidtickets === null){
            this._unpaidtickets = this.tickets.filter(
            (ticket) => ticket.IsValid
                && !ticket.IsPaid
                && !ticket.IsToPay
                && ticket.IsRecent);
        }

        return this._unpaidtickets;
    }

    /**
     * Tickets with a pending payment request (`IsToPay`: waiter cash/card or
     * Stripe `PAYLINE`) still waiting to be confirmed or cancelled.
     */
    get ToPayTickets(): Array <TicketView> {
        if (this._topaytickets === null){
            this._topaytickets = this.tickets.filter(
            (ticket) => ticket.IsValid
                && ticket.IsToPay
                && !ticket.IsPaid
                && ticket.IsRecent);
        }

        return this._topaytickets;
    }

    /** Tickets of this table paid by the current session and still recent (guest receipts). */
    get PaidTickets(): Array <TicketView> {
        if (this._paidtickets === null){
            this._paidtickets = this.tickets.filter(
            (ticket) => ticket.PaidInSession && ticket.IsRecent);
        }

        return this._paidtickets;
    }

    /** Tickets of this table that are ready for the current session and not yet notified. */
    get ReadyTickets(): Array <TicketView> {
        if (this._readytickets === null){
            this._readytickets = this.tickets.filter(
            (ticket) => !ticket.IsNotifiedFlag && ticket.InSession && ticket.IsReady);
        }

        return this._readytickets;
    }

    get diners(): number {
        if (this._validdiners === null){
            const nums = this.tickets
                .filter((ticket) => ticket.IsRecent && ticket.IsOpened)
                .map((ticket) => ticket.diners)
                .filter((diners): diners is number => diners !== null && diners !== 0);

            if (nums.length === 0){
                this._validdiners = 0;
            }
            else {
                nums.sort((a, b) => a - b);
                this._validdiners = nums[nums.length - 1];
            }
        }

        return this._validdiners;
    }

    /** Valid requests already related on this table in storage (guest graph is session-scoped). */
    get requests(): Array <AskWaiterView> {
        if (this._validrequests === null){
            const _requests: Array <AskWaiterView> = [];
            for (const _request of this.qrcode.requests){
                if (_request.IsValid){
                    const _requestview = this.alives.get(_request) as AskWaiterView;
                    if (_requestview){
                        _requests.push(_requestview);
                    }
                }
            }
            this._validrequests = _requests;
        }
        return this._validrequests;
    }

    get WaiterReason(): 'GENERIC' | 'REFUND' | 'PAYCASH' | 'PAYCARD' | null {
        if (this.requests.length === 0){
            return null;
        }
        return (this.WaiterAmount === 0) ? 'GENERIC' : ((this.WaiterAmount < 0) ? 'REFUND' : this._payMethod());
    }

    private _payMethod(): 'PAYCASH' | 'PAYCARD' {
        let _method: 'PAYCASH' | 'PAYCARD' = 'PAYCASH';
        for (const _request of this.requests){
            if (_request.ticket && (_request.reason === 'PAYCARD')){
                _method = 'PAYCARD';
            }
        }
        return _method;
    }

    get WaiterAmount(): number {
        let _toPayment = 0;
        let _toRefunds = 0;

        for (const _request of this.requests){
            if (!_request.ticket){
                continue;
            }

            switch (_request.reason){
                case 'REFUND':
                    _toRefunds += _request.amount || 0;
                    break;

                case 'PAYCASH':
                case 'PAYCARD':
                    _toPayment += _request.ticket.price || 0;
                    break;
            }
        }

        return Math.round((_toPayment - _toRefunds) * 100) / 100;
    }

    /**********************************/
    /* CUSTOM METHODS                 */
    /**********************************/

    get IsPending(): boolean{
        return this.qrcode.IsPending;
    }

    QrCodeUrl(language: string): string | null {
        return this.qrcode.QrCodeUrl(language);
    }

    private static readonly _QR_IMAGE_WIDTH = 420;
    private static readonly _QR_IMAGE_MARGIN = 1;
    /** Mark side relative to QR canvas width (occlusion budget ≤ ~25 %). */
    private static readonly _QR_LOGO_RATIO = 0.22;
    /** Extra white eye padding around the mark, relative to mark side. */
    private static readonly _QR_LOGO_PAD_RATIO = 0.12;

    /**
     * Builds a PNG data URL for this table's guest QR.
     * Always uses error correction `H`. When `options.logoDataUrl` is set, draws the mark
     * centered on an opaque light eye; on logo failure returns a plain QR and logs a warning.
     */
    async QrCodeImageDataUrl(language: string, options?: QrCodeImageOptions): Promise<string | null> {
        const _url = this.QrCodeUrl(language);
        if (!_url){
            return null;
        }

        const _qrOpts = {
            width: _QrCodeView._QR_IMAGE_WIDTH,
            margin: _QrCodeView._QR_IMAGE_MARGIN,
            errorCorrectionLevel: 'H' as const
        };

        const _logo = options?.logoDataUrl || null;
        if (!_logo){
            return await toDataURL(_url, _qrOpts);
        }

        try {
            return await this._ComposeQrWithLogo(_url, _logo, _qrOpts);
        }
        catch (error){
            console.warn('[QRCODE] logo composition failed; returning plain QR', error);
            return await toDataURL(_url, _qrOpts);
        }
    }

    /**
     * Renders the QR onto a canvas and draws the mark in the center.
     */
    private async _ComposeQrWithLogo(
        url: string,
        logoDataUrl: string,
        qrOpts: { width: number; margin: number; errorCorrectionLevel: 'H' }
    ): Promise<string> {
        const _canvas = await toCanvas(url, qrOpts);
        const _ctx = _canvas.getContext('2d');
        if (!_ctx){
            throw new Error('QR canvas 2d context unavailable');
        }

        const _img = await _QrCodeView._LoadLogoImage(logoDataUrl);
        const _side = Math.min(_canvas.width, _canvas.height);
        const _logoSize = Math.round(_side * _QrCodeView._QR_LOGO_RATIO);
        const _pad = Math.round(_logoSize * _QrCodeView._QR_LOGO_PAD_RATIO);
        const _eye = _logoSize + (_pad * 2);
        const _x = Math.round((_canvas.width - _eye) / 2);
        const _y = Math.round((_canvas.height - _eye) / 2);

        _ctx.fillStyle = '#ffffff';
        _ctx.fillRect(_x, _y, _eye, _eye);
        _ctx.drawImage(_img, _x + _pad, _y + _pad, _logoSize, _logoSize);

        return _canvas.toDataURL('image/png');
    }

    private static _LoadLogoImage(dataUrl: string): Promise<CanvasImageSource> {
        return new Promise((resolve, reject) => {
            const _img = new Image();
            _img.onload = () => resolve(_img);
            _img.onerror = () => reject(new Error('QR logo image failed to load'));
            _img.src = dataUrl;
        });
    }

    get Attention(): boolean {
        return this.requests.some((request) => request.IsRecent);
    }

    /**
     * Aggregated pending payment request for this table (epic 10 / ptf).
     * Built from recent PAYCASH/PAYCARD asks whose ticket is still in {@link ToPayTickets}.
     */
    get PayRequest(): PayRequest | null {
        const _toPay = new Set(this.ToPayTickets);
        const _asks = this.requests
            .filter((ask) =>
                (ask.reason === 'PAYCASH' || ask.reason === 'PAYCARD')
                && ask.IsRecent
                && ask.ticket != null
                && _toPay.has(ask.ticket)
            )
            .sort((a, b) => {
                const _ta = (a.created as Date | null)?.getTime() ?? 0;
                const _tb = (b.created as Date | null)?.getTime() ?? 0;
                return _ta - _tb;
            });

        if (_asks.length === 0){
            return null;
        }

        const _tickets: TicketView[] = [];
        let _method: 'PAYCASH' | 'PAYCARD' = 'PAYCASH';
        let _total = 0;

        for (const _ask of _asks){
            const _ticket = _ask.ticket!;
            if (_tickets.includes(_ticket)){
                continue;
            }

            _tickets.push(_ticket);
            _total += _ticket.due || 0;
            
            if (_ask.reason === 'PAYCARD'){
                _method = 'PAYCARD';
            }
        }

        return {
            table: this.alives.get(this.qrcode) as QrCodeView,
            tickets: _tickets,
            total: Math.round(_total * 100) / 100,
            method: _method,
            since: (_asks[0].created as Date | null) ?? null,
        };
    }

    /**
     * True when a guest QR session on this table has status AC.
     */
    get IsConnected(): boolean {
        if (this._isconnected === null){
            const _scan = this.qrcode.qrscan;
            this._isconnected = !!_scan && _scan.sessions.some((session) => session.IsActive);
        }

        return this._isconnected;
    }

    get IsCfgError(): boolean {
        return false;    // TODO
    }

    get IsBusy(): boolean {
        return this.tickets.some((ticket) => {
            return ticket.IsRecent && ticket.IsOpened;
        });
    }

    get Paused(): boolean {
        return false;    // TODO
    }

    /**********************************/
    /* SELECTED CONTROL               */
    /**********************************/    

    private _checked = false;
    get Checked(): boolean {
        return this._checked;
    }

    set Checked(value: boolean){
        this._checked = value;
        this.qrcode.DoRefresh('QRCODECHECKED');
    }

    /**********************************/
    /* TABLE THUMBNAIL INFORMATION    */
    /**********************************/    

    private get _status(): TableStatus {
        if (this.IsPending){
            return 'wait';
        }

        if (this.IsCfgError){
            return 'cnfg';
        }

        if (this.Attention){
            return 'goto';
        }

        if (this.IsBusy){
            return 'busy';
        }

        if (this.Paused){
            return 'work';
        }

        return 'free';
    }

    get Status(): TableStatus {
        if (this._tablestat == null){
            this._tablestat = this._status;
        }
        return this._tablestat;
    }

    get thumb(): TableThumbInfo {
        if (this._thumbinfo == null){

            const _thumb: Record<TableStatus, any> = {
                wait: { icon: '', color: '' },
                cnfg: { icon: 'settings-outline', color: 'danger' },
                goto: { icon: 'hand-right-outline', color: 'warning' },
                busy: { icon: 'people-circle', color: 'success' },
                work: { icon: 'pause-circle-outline', color: 'medium' },
                free: { icon: 'scan-circle', color: 'medium' }
            }

            const _thumbinfo = _thumb[this.Status];
            this._thumbinfo = {
                icon: _thumbinfo.icon,
                color: _thumbinfo.color,
            }
        }

        return this._thumbinfo;
    }
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type QrCodeView = ViewType <_QrCodeView, QrCode>;

export function QrCodeViewProxy(object: QrCode, data: dataService): QrCodeView {
    return new _QrCodeView(object, data).Proxy as QrCodeView;
}
